import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { showError, showSuccess } from '../../toast';
import { api } from '../../api';
import { t } from '../../i18n';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Screen, Title } from '../../ui';

// дд.мм.гггг ⇄ ISO — същият формат както при фактурите
function todayBg(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function parseBgDate(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  // UTC, за да не се измества денят при записа
  const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  if (d.getUTCDate() !== Number(dd) || d.getUTCMonth() !== Number(mm) - 1) return null;
  return d.toISOString();
}

export default function AddTenantScreen({ route, navigation }: any) {
  const { apartmentId } = route.params;
  const [phone, setPhone] = useState('');
  const [rent, setRent] = useState('');
  const [dueDay, setDueDay] = useState('5');
  const [deposit, setDeposit] = useState('');
  const [depositMethod, setDepositMethod] = useState<'BANK' | 'CASH'>('BANK');
  const [depositPaidNow, setDepositPaidNow] = useState(false);
  const [startDate, setStartDate] = useState(todayBg());
  const [termMonths, setTermMonths] = useState('12');
  // Период на плащане за договора (Член 7): „от X-то до Y-то число“
  const [payFromDay, setPayFromDay] = useState('1');
  const [payToDay, setPayToDay] = useState('5');
  const [prorata, setProrata] = useState<any>(null);
  const [prorataAmount, setProrataAmount] = useState('');
  const [useProrata, setUseProrata] = useState(true);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<any | null>(null);

  const hasDeposit = deposit.trim().length > 0 && Number(deposit.replace(',', '.')) > 0;

  // Сметката за първия непълен период се пита от сървъра, щом има
  // наем, падеж и начална дата — за да види наемодателят какво излиза.
  useEffect(() => {
    const iso = parseBgDate(startDate);
    const rentCents = Math.round(Number(rent.replace(',', '.')) * 100);
    if (!iso || !rentCents || !dueDay) {
      setProrata(null);
      return;
    }
    const q = new URLSearchParams({
      monthlyRentCents: String(rentCents),
      dueDay,
      startDate: iso,
    });
    api(`/apartments/${apartmentId}/prorata?${q}`)
      .then((r) => {
        setProrata(r.prorata);
        if (r.prorata.applies) setProrataAmount((r.prorata.amountCents / 100).toFixed(2));
      })
      .catch(() => setProrata(null));
  }, [apartmentId, rent, dueDay, startDate]);

  // История и ревюта на кандидата — ПРЕДИ да бъде добавен
  const checkHistory = async () => {
    try {
      setHistory(await api(`/reviews/tenant-history?phone=${encodeURIComponent(phone)}`));
    } catch (e: any) {
      showError(e.message);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await api(`/apartments/${apartmentId}/tenants`, {
        body: {
          phone,
          monthlyRentCents: Math.round(Number(rent.replace(',', '.')) * 100),
          dueDay: Number(dueDay),
          ...(parseBgDate(startDate) ? { startDate: parseBgDate(startDate) } : {}),
          ...(termMonths ? { termMonths: Number(termMonths) } : {}),
          ...(payFromDay ? { rentPayFromDay: Number(payFromDay) } : {}),
          ...(payToDay ? { rentPayToDay: Number(payToDay) } : {}),
          ...(useProrata && prorata?.applies && prorataAmount
            ? { prorataCents: Math.round(Number(prorataAmount.replace(',', '.')) * 100) }
            : {}),
          ...(hasDeposit
            ? {
                depositCents: Math.round(Number(deposit.replace(',', '.')) * 100),
                depositMethod,
                depositPaidNow: depositMethod === 'CASH' && depositPaidNow,
              }
            : {}),
        },
      });
      if (res.depositReceipt) {
        showSuccess(t('Наемателят е добавен. Бележка за депозита е издадена в „Документи“.'));
      } else {
        showSuccess(t('Наемателят е добавен. Той ще може да се регистрира с този номер.'));
      }
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Добавяне на наемател')}</Title>
      <Card>
        <Muted>
          {t('Въвеждането на номера отключва регистрацията на наемателя. Той ще получи SMS с покана.')}
        </Muted>
        <Label>{t('Телефон на наемателя')}</Label>
        <Input value={phone} onChangeText={setPhone} keyboardType="phone-pad" placeholder="+359 88 812 3456" />
        <Button title={t('Провери историята му 🔎')} variant="secondary" onPress={checkHistory} disabled={phone.length < 6} />

        {history && !history.found && (
          <View style={{ marginTop: 8, padding: 10, backgroundColor: COLORS.bg, borderRadius: 8, borderWidth: 1, borderColor: COLORS.border }}>
            <Text style={{ fontWeight: '600', color: COLORS.text }}>{t('✓ Проверено — чист номер')}</Text>
            <Muted>
              {t('Няма регистриран наемател, история или ревюта за този номер. След добавянето му кажете на наемателя да свали Livupp и да влезе от „Първи вход" с този номер — кодът идва по SMS.')}
            </Muted>
          </View>
        )}
        {history?.found && (
          <View style={{ marginTop: 8, padding: 10, backgroundColor: COLORS.bg, borderRadius: 8 }}>
            <Text style={{ fontWeight: '600', color: COLORS.text }}>
              {history.tenant.firstName} {history.tenant.lastName}
              {history.avgRating ? ` · ★ ${history.avgRating.toFixed(1)}` : ` · ${t('без ревюта')}`}
            </Text>
            {history.history.map((h: any, i: number) => (
              <Muted key={i}>
                {h.city}: {t('{n} мес.', { n: h.months })}{h.current ? ` ${t('(текуща квартира!)')}` : ''}
              </Muted>
            ))}
            {history.reviews.slice(0, 3).map((r: any, i: number) => (
              <Muted key={`r${i}`}>★{r.rating} „{r.comment ?? '—'}“ — {r.from}</Muted>
            ))}
            {history.history.some((h: any) => h.current) && (
              <Badge text={t('Внимание: има активно наемно отношение')} color="#D97706" />
            )}
          </View>
        )}
        <Label>{t('Месечен наем (€)')}</Label>
        <Input value={rent} onChangeText={setRent} keyboardType="decimal-pad" placeholder="400" />
        <Label>{t('Падежен ден от месеца')}</Label>
        <Input value={dueDay} onChangeText={setDueDay} keyboardType="number-pad" placeholder="5" />

        <Label>{t('Наемът се плаща от – до число (за договора)')}</Label>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <View style={{ flex: 1 }}>
            <Input value={payFromDay} onChangeText={setPayFromDay} keyboardType="number-pad" placeholder="1" />
          </View>
          <View style={{ flex: 1 }}>
            <Input value={payToDay} onChangeText={setPayToDay} keyboardType="number-pad" placeholder="5" />
          </View>
        </View>
        <Muted>{t('Влиза в Член 7 на договора: „наем от 1-то до 5-то число“.')}</Muted>

        <Label>{t('Начало на договора')}</Label>
        <Input value={startDate} onChangeText={setStartDate} placeholder={t('дд.мм.гггг')} />
        <Label>{t('Срок (месеци)')}</Label>
        <Input value={termMonths} onChangeText={setTermMonths} keyboardType="number-pad" placeholder="12" />
        <Muted>{t('Оставете празно за безсрочен договор.')}</Muted>

        {/* Първи непълен период — предложение, което може да се смени */}
        {prorata?.applies && (
          <View style={{ marginTop: 12, padding: 12, backgroundColor: COLORS.bg, borderRadius: 10 }}>
            <Label>{t('Наем за първия непълен период')}</Label>
            <Muted>{prorata.explanation}</Muted>
            <Pressable
              onPress={() => setUseProrata((v) => !v)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 }}
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  borderWidth: 2,
                  borderColor: useProrata ? COLORS.primary : COLORS.muted,
                  backgroundColor: useProrata ? COLORS.primary : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {useProrata && <Text style={{ color: '#fff', fontWeight: '700' }}>✓</Text>}
              </View>
              <Text style={{ color: COLORS.text, flex: 1 }}>{t('Начисли наем за непълния период')}</Text>
            </Pressable>
            {useProrata && (
              <>
                <Label>{t('Сума (€)')}</Label>
                <Input
                  value={prorataAmount}
                  onChangeText={setProrataAmount}
                  keyboardType="decimal-pad"
                  placeholder={(prorata.amountCents / 100).toFixed(2)}
                />
                <Muted>
                  {t('Изчислено: {rate} €/ден × {days} дни. Може да въведете и друга сума по ваша уговорка.', { rate: (prorata.dailyRateCents / 100).toFixed(2), days: prorata.days })}
                </Muted>
              </>
            )}
          </View>
        )}

        <Label>{t('Депозит (€, по избор)')}</Label>
        <Input value={deposit} onChangeText={setDeposit} keyboardType="decimal-pad" placeholder="400" />

        {hasDeposit && (
          <>
            <Label>{t('Как ще се преведе депозитът?')}</Label>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(
                [
                  ['BANK', t('По банка')],
                  ['CASH', t('В брой')],
                ] as const
              ).map(([key, lbl]) => (
                <Pressable
                  key={key}
                  onPress={() => setDepositMethod(key)}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    alignItems: 'center',
                    backgroundColor: depositMethod === key ? COLORS.primary : COLORS.bg,
                    borderWidth: 1,
                    borderColor: depositMethod === key ? COLORS.primary : COLORS.border,
                  }}
                >
                  <Text style={{ color: depositMethod === key ? '#fff' : COLORS.text }}>{lbl}</Text>
                </Pressable>
              ))}
            </View>

            {depositMethod === 'CASH' && (
              <Pressable
                onPress={() => setDepositPaidNow((v) => !v)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 }}
              >
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    borderWidth: 2,
                    borderColor: depositPaidNow ? COLORS.primary : COLORS.muted,
                    backgroundColor: depositPaidNow ? COLORS.primary : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {depositPaidNow && <Text style={{ color: '#fff', fontWeight: '700' }}>✓</Text>}
                </View>
                <Text style={{ color: COLORS.text, flex: 1 }}>{t('Депозитът вече е получен в брой')}</Text>
              </Pressable>
            )}
            {depositMethod === 'CASH' && depositPaidNow && (
              <Muted>{t('Ще се издаде бележка (разписка) за депозита в „Документи“.')}</Muted>
            )}
            {depositMethod === 'BANK' && (
              <Muted>
                {t('Депозитът става задължение на наемателя с отделен код за основание — плаща се като наема и може да приложи снимка на платежното.')}
              </Muted>
            )}
          </>
        )}

        <Button
          title={t('Добави наемател')}
          onPress={submit}
          disabled={!phone || !rent || !dueDay}
          loading={loading}
        />
      </Card>
    </Screen>
  );
}
